/**
 * The capability catalogue (TO-368, capability `brand-design-configuration`, tech spec §9/§20):
 * registry **metadata** for every configurable design slot. It is data only — no React — so the
 * compiler, Brand Studio and the UI registries share one list (ADR-0045 package mapping). The
 * compiler's default `designRegistry`, `listDesignOptions` and the per-slot ID unions are all
 * derived from it.
 */
import type { DesignConfiguration } from '../brand.types.js'

/** Lifecycle of one registered capability (ADR-0045 D14). */
export enum CapabilityStatus {
	/** Offered only to `draft` / `ready-for-review` brands; blocks `approved` and production. */
	Experimental = 'experimental',
	Stable = 'stable',
	/** Keeps resolving for brands already on it, with a warning. */
	Deprecated = 'deprecated',
}

/** The three groups of `design.json`. */
export enum DesignGroup {
	Components = 'components',
	Patterns = 'patterns',
	Layouts = 'layouts',
}

/** Metadata of one registered capability (tech spec §20). */
export interface CapabilityEntry {
	/** Stable, generic, kebab-case ID — the value a brand selects in `design.json`. */
	readonly id: string
	/** Human-readable name shown in Brand Studio. */
	readonly label: string
	readonly status: CapabilityStatus
	/** Name of the exported TypeScript props interface the implementation accepts. */
	readonly propsContract: string
	/** Storybook story ID that renders this capability alone. */
	readonly story: string
	/** For a `deprecated` entry: the ID of the `stable` entry of the same slot that replaces it. */
	readonly replacedBy?: string
}

/** Codes of {@link catalogueIntegrity} (design-system-extension REQ-001). */
export enum CatalogueViolationCode {
	DefaultNotStable = 'CATALOGUE_DEFAULT_NOT_STABLE',
	ReplacementMissing = 'CATALOGUE_REPLACEMENT_MISSING',
	ReplacementInvalid = 'CATALOGUE_REPLACEMENT_INVALID',
}

/** One lifecycle-metadata inconsistency of a catalogue entry. */
export interface CatalogueViolation {
	readonly code: CatalogueViolationCode
	/** `<group>.<slot>`. */
	readonly slotKey: string
	readonly entryId: string
}

/** One configurable slot: its entries in catalogue order and the ID used when a brand selects none. */
export interface CapabilitySlot {
	readonly defaultId: string
	readonly entries: readonly CapabilityEntry[]
}

/** The whole catalogue: group → slot → {@link CapabilitySlot}. */
export type DesignCatalogue = Readonly<Record<DesignGroup, Readonly<Record<string, CapabilitySlot>>>>

/** One Brand Studio option, derived from a catalogue entry. */
export interface DesignOption {
	readonly id: string
	readonly label: string
	readonly status: CapabilityStatus
	readonly story: string
	readonly isDefault: boolean
}

type EntrySpec = readonly [id: string, label: string, status?: CapabilityStatus, replacedBy?: string]

/** One slot from compact entry specs; every story ID is `<storyPrefix>--<id>` (Storybook's ID rule). */
function slot<const E extends readonly EntrySpec[]>(
	defaultId: E[number][0],
	propsContract: string,
	storyPrefix: string,
	specs: E,
): {
	readonly defaultId: E[number][0]
	readonly entries: { readonly [K in keyof E]: CapabilityEntry & { readonly id: E[K][0] } }
} {
	const entries = specs.map(([id, label, status = CapabilityStatus.Stable, replacedBy]) =>
		Object.freeze({
			id,
			label,
			status,
			propsContract,
			story: `${storyPrefix}--${id}`,
			...(replacedBy === undefined ? {} : { replacedBy }),
		}),
	)
	return Object.freeze({ defaultId, entries: Object.freeze(entries) }) as never
}

/** The registered capabilities. Order is catalogue order (Brand Studio lists options in it). */
export const DESIGN_CATALOGUE = Object.freeze({
	components: Object.freeze({
		button: slot('solid', 'ButtonProps', 'components-actions-button', [
			['solid', 'Solid'],
			['soft', 'Soft'],
			['outline', 'Outline'],
		]),
		card: slot('bordered', 'CardShellProps', 'components-content-cardshell', [
			['bordered', 'Bordered'],
			['flat', 'Flat'],
			['elevated', 'Elevated'],
		]),
		navigation: slot('standard', 'NavigationProps', 'components-navigation-navigation', [
			['standard', 'Standard'],
			['minimal', 'Minimal'],
		]),
	}),
	patterns: Object.freeze({
		hero: slot('centered', 'HeroProps', 'patterns-hero', [
			['centered', 'Centered'],
			['split', 'Split'],
			['full-bleed', 'Full bleed'],
			['floating-media', 'Floating media', CapabilityStatus.Experimental],
		]),
		productCard: slot('vertical', 'ProductCardProps', 'patterns-product-card', [
			['vertical', 'Vertical'],
			['horizontal', 'Horizontal'],
			['compact', 'Compact'],
		]),
		header: slot('standard', 'HeaderProps', 'patterns-header', [
			['standard', 'Standard'],
			['centered', 'Centered'],
		]),
		footer: slot('standard', 'FooterProps', 'patterns-footer', [
			['standard', 'Standard'],
			['minimal', 'Minimal'],
			['extended', 'Extended'],
		]),
	}),
	layouts: Object.freeze({
		default: slot('corporate', 'DefaultLayoutProps', 'layouts-default', [
			['corporate', 'Corporate'],
			['editorial', 'Editorial'],
			['minimal', 'Minimal'],
		]),
		campaign: slot('campaign', 'CampaignLayoutProps', 'layouts-campaign', [['campaign', 'Campaign']]),
		product: slot('product', 'ProductLayoutProps', 'layouts-product', [['product', 'Product']]),
	}),
}) satisfies DesignCatalogue

type Catalogue = typeof DESIGN_CATALOGUE
type IdOf<G extends keyof Catalogue, K extends keyof Catalogue[G]> = Catalogue[G][K] extends {
	entries: readonly (infer E)[]
}
	? E extends { id: infer I }
		? I
		: never
	: never

/** `components.button` IDs. */
export type ButtonAppearanceId = IdOf<'components', 'button'>
/** `components.card` IDs. */
export type CardAppearanceId = IdOf<'components', 'card'>
/** `components.navigation` IDs. */
export type NavigationAppearanceId = IdOf<'components', 'navigation'>
/** `patterns.hero` IDs. */
export type HeroId = IdOf<'patterns', 'hero'>
/** `patterns.productCard` IDs. */
export type ProductCardId = IdOf<'patterns', 'productCard'>
/** `patterns.header` IDs. */
export type HeaderId = IdOf<'patterns', 'header'>
/** `patterns.footer` IDs. */
export type FooterId = IdOf<'patterns', 'footer'>
/** `layouts.default` IDs. */
export type DefaultLayoutId = IdOf<'layouts', 'default'>
/** `layouts.campaign` IDs. */
export type CampaignLayoutId = IdOf<'layouts', 'campaign'>
/** `layouts.product` IDs. */
export type ProductLayoutId = IdOf<'layouts', 'product'>

/** The slot names of a group, typed from the catalogue. */
export type DesignSlot<G extends DesignGroup> = keyof Catalogue[G] & string

/** `Object.hasOwn` for the ES2020 lib the UI packages compile against. */
const hasOwn = (target: object, key: string): boolean => Object.getOwnPropertyDescriptor(target, key) !== undefined

/** The slot of a group, or `undefined` for an unknown group/slot (own properties only). */
export function findCapabilitySlot(
	catalogue: DesignCatalogue,
	group: string,
	slot: string,
): CapabilitySlot | undefined {
	if (!hasOwn(catalogue, group)) return undefined
	const slots = catalogue[group as DesignGroup]
	return hasOwn(slots, slot) ? slots[slot] : undefined
}

/**
 * The options of one slot in catalogue order, for Brand Studio. Throws `RangeError` for an
 * unknown group or slot.
 */
export function listDesignOptions(
	group: DesignGroup | `${DesignGroup}`,
	slot: string,
	catalogue: DesignCatalogue = DESIGN_CATALOGUE,
): DesignOption[] {
	const found = findCapabilitySlot(catalogue, group, slot)
	if (found === undefined) throw new RangeError(`Unknown design slot "${group}.${slot}"`)
	return found.entries.map(({ id, label, status, story }) => ({
		id,
		label,
		status,
		story,
		isDefault: id === found.defaultId,
	}))
}

/**
 * The brand's selection for a slot, or the slot's default when the brand selects none (or when
 * `design` is `null`, i.e. no brand is bound). No status check.
 */
export function resolveDesignSelection(
	design: DesignConfiguration | null,
	group: DesignGroup | `${DesignGroup}`,
	slot: string,
	catalogue: DesignCatalogue = DESIGN_CATALOGUE,
): string {
	const selections = design?.[group]
	if (selections !== undefined && hasOwn(selections, slot)) return selections[slot]
	const found = findCapabilitySlot(catalogue, group, slot)
	if (found === undefined) throw new RangeError(`Unknown design slot "${group}.${slot}"`)
	return found.defaultId
}

/** The violation code of one entry, or `null` (REQ-001). */
function entryViolation(slot: CapabilitySlot, entry: CapabilityEntry): CatalogueViolationCode | null {
	if (entry.id === slot.defaultId && entry.status !== CapabilityStatus.Stable) {
		return CatalogueViolationCode.DefaultNotStable
	}
	if (entry.replacedBy === undefined) {
		return entry.status === CapabilityStatus.Deprecated ? CatalogueViolationCode.ReplacementMissing : null
	}
	const replacement = slot.entries.find((candidate) => candidate.id === entry.replacedBy)
	const valid = entry.status === CapabilityStatus.Deprecated && replacement?.status === CapabilityStatus.Stable
	return valid ? null : CatalogueViolationCode.ReplacementInvalid
}

/**
 * Lifecycle-metadata violations of a catalogue, in catalogue order (design-system-extension
 * REQ-001): a slot default must be `stable`, a `deprecated` entry must name a `stable` entry of
 * its slot as `replacedBy`, and only a `deprecated` entry may name one.
 */
export function catalogueIntegrity(catalogue: DesignCatalogue): CatalogueViolation[] {
	return Object.values(DesignGroup).flatMap((group) =>
		Object.entries(catalogue[group]).flatMap(([name, slot]) =>
			slot.entries.flatMap((entry) => {
				const code = entryViolation(slot, entry)
				return code === null ? [] : [{ code, slotKey: `${group}.${name}`, entryId: entry.id }]
			}),
		),
	)
}
